import React, { useEffect, useState } from 'react'
import {
  Chip,
  CircularProgress,
  FormControl,
  makeStyles,
  MenuItem,
  Paper,
  Select,
  Typography,
  IconButton,
  Tooltip,
  DialogContent,
  Dialog,
  Button
} from '@material-ui/core'
import { Delete, Download, Edit, Warning } from '@material-ui/icons'
import { Box } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import speakerSlice, { getSpeakers } from 'store/speaker'
import { caseInsensitiveSort } from 'utils'
import pollingSlice, {
  deletePollById,
  fetchPollQuestions,
  getConferenceBasedPolls
} from 'store/polling'
import BasicTable from 'components/BasicTable'
import ResourceNotFound from 'components/ResourceNotFound'
import Wrapper from 'components/Wrapper'
import getTableOptions from 'utils/getTableOptions'
import { getPollStatusText, getPollType } from 'utils/polling'
import { downloadPollResultsInCSV } from 'utils/downloadCSV'
import PollingForm from 'views/admin/subjects/PollingForm/PollingForm'
import { PollStatus, Roles, speakerStatus } from 'constants/index'
import { isEqual, sortBy, first } from 'lodash'
import { renderCol } from 'utils/renderColumns'
import { colors } from 'utils/colors'
import talkSlice from 'store/talk'
import moment from 'moment-timezone'
import { ALL_DATES_FILTER } from 'store/event'
import { convertTimestampToLocalString } from 'utils/times'
import TextField from '@material-ui/core/TextField'
import Autocomplete from '@material-ui/lab/Autocomplete'
import CopyButton from 'components/CopyButton'
import { nanoid } from 'nanoid'
import ConfirmModal from '../../../components/ConfirmationModal'
import { toast } from 'react-toastify'

const app_base_url = window.location.origin

const emptyQuestion = {
  title: '',
  options: [
    { optId: nanoid(), opt: '' },
    { optId: nanoid(), opt: '' }
  ]
}
const pollInitialValues = {
  title: '',
  speakerId: '',
  questions: [emptyQuestion]
}

const PollsListing = () => {
  const dispatch = useDispatch()
  const classes = useStyles()
  const options = getTableOptions()

  const [loading, setLoading] = useState(false)
  const [questionsLoading, setQuestionsLoading] = useState(false)
  const [editedPoll, setEditedPoll] = useState(null)
  const [confirmModal, setConfirmModal] = useState(false)
  const [deletedPollId, setDeletedPollId] = useState('')
  const [isDeletingPoll, setIsDeletingPoll] = useState(false)
  const [pollValues, setPollValues] = useState(pollInitialValues)
  const [dateFilters, setDateFilters] = useState([ALL_DATES_FILTER])
  const { user } = useSelector((state) => state.auth)
  const { speakers } = useSelector((state) => state.speaker)
  const {
    polls,
    confFilter,
    selectedSpeaker,
    selectedDate,
    selectedStatus,
    currentPage,
    pageSize
  } = useSelector((state) => state.polling)

  const [openAddPollModal, setOpenAddPollModal] = useState(false)

  const {
    conferences,
    conference,
    inProgress: confProgress
  } = useSelector((state) => state.conference)
  const activeConferences = conferences?.filter((row) => row.isActive && row?.havePolls)
  const orderedConferences = caseInsensitiveSort(activeConferences, 'confName')
  const [formattedPolls, setFormattedPolls] = useState(polls || [])

  const handleCloseDeleteModal = () => {
    setDeletedPollId('')
  }

  const handleDeletePoll = async () => {
    setIsDeletingPoll(true)
    const isDeleted = await dispatch(deletePollById(deletedPollId, { validateChecks: true }))
    if (isDeleted) {
      dispatch(pollingSlice.actions.filterDeletedPolls({ pollId: deletedPollId }))
      toast.success('The poll along with the questions deleted successfully.')
    }
    setIsDeletingPoll(false)
  }

  useEffect(() => {
    if (confFilter && user?.role === Roles.SUPER_ADMIN && !confProgress) {
      conferences.every((conf) => {
        if (conf?.id === confFilter && conf?.isActive === false) {
          dispatch(pollingSlice.actions.setConferenceFilter(first(orderedConferences)?.id))
          return false
        }
        return true
      })
    }
  }, [confProgress, confFilter])

  useEffect(() => {
    if (user.role === Roles.SUPER_ADMIN && !confFilter && !confProgress) {
      dispatch(pollingSlice.actions.setConferenceFilter(first(orderedConferences)?.id))
    }
  }, [confProgress, confFilter])

  const handleCloseModal = (formValues) => {
    if (isEqual(pollValues, formValues)) {
      setOpenAddPollModal(false)
      setEditedPoll(null)
      setPollValues(pollInitialValues)
    } else {
      setConfirmModal(true)
    }
  }

  const handleCloseConfirmModal = () => {
    setConfirmModal(false)
    setEditedPoll(null)
    setOpenAddPollModal(false)
    setPollValues(pollInitialValues)
  }

  const handleOpenModalForEdit = async (poll) => {
    setQuestionsLoading(true)
    setEditedPoll(poll)
    const questions = await fetchPollQuestions(poll?.id)
    questions.forEach((question) => sortBy(question.options, 'order'))
    setPollValues({
      title: poll?.title,
      questions: questions
    })
    setQuestionsLoading(false)
  }

  const tableHead = [
    {
      name: 'Sr #',
      options: {
        customBodyRender: (value) => <div style={{ minWidth: '90px' }}>{value}</div>
      }
    },
    'Poll Title',
    'Poll Type',
    {
      name: 'Event/Session/Talk',
      options: {
        sort: false
      }
    },
    {
      name: 'Speaker Name',
      options: {
        sort: false
      }
    },
    'Status',
    {
      name: 'Actions',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => renderCol(value),
        customHeadLabelRender: (meta) => renderCol(meta.label)
      }
    }
  ]

  const formatTableData = (polls) =>
    polls?.map((poll) => {
      const conf =
        user?.role === Roles.ADMIN
          ? conference
          : conferences?.find((conf) => conf?.id === confFilter)
      const confTimezone = conf?.timezone
      const conf24HrFormat = conf?.is24hrFormat
      const { status, bgColor, color } = getPollStatusText(poll?.status)
      return [
        poll?.srNo,
        poll?.title,
        getPollType(poll),
        <Box component="span">
          <Typography variant="body2">{poll?.associatedTitle}</Typography>
          {poll?.startDate && (
            <Typography variant="body2" className={classes.subText}>
              {convertTimestampToLocalString(poll?.startDate, confTimezone, conf24HrFormat)} -{' '}
              {convertTimestampToLocalString(poll?.endDate, confTimezone, conf24HrFormat)}
            </Typography>
          )}
        </Box>,
        poll?.speaker ?? 'N/A',
        <Chip label={status} style={{ backgroundColor: bgColor, color, width: '90px' }} />,
        <Box component="span">
          <Tooltip title="Edit Poll">
            <IconButton className={classes.iconButton} onClick={() => handleOpenModalForEdit(poll)}>
              <Edit />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Poll">
            <IconButton className={classes.iconButton} onClick={() => setDeletedPollId(poll?.id)}>
              <Delete />
            </IconButton>
          </Tooltip>
          {poll?.status === PollStatus.CLOSED && (
            <Tooltip title="Download Results in CSV">
              <IconButton
                className={classes.iconButton}
                onClick={() => downloadPollResultsInCSV(poll)}>
                <Download />
              </IconButton>
            </Tooltip>
          )}
          {poll?.isAssociated && poll?.status !== PollStatus.CLOSED && (
            <CopyButton
              value={`${app_base_url}/polls/${poll?.id}`}
              type="icon"
              tooltip="Copy poll link"
            />
          )}
        </Box>
      ]
    })

  useEffect(() => {
    setLoading(true)
    const unsub = getSpeakers(
      user?.role === Roles.SUPER_ADMIN && confFilter ? confFilter : user?.conferenceId,
      (data) => {
        dispatch(speakerSlice.actions.setSpeakers(caseInsensitiveSort(data, 'fullName')))
      }
    )
    return () => unsub && unsub()
  }, [dispatch, user?.conferenceId, confFilter])

  useEffect(() => {
    setLoading(true)
    getConferenceBasedPolls(
      user?.role === Roles.SUPER_ADMIN && confFilter ? confFilter : user?.conferenceId
    ).then((data) => {
      dispatch(pollingSlice.actions.setPolls(data))
      setLoading(false)
    })
  }, [dispatch, confFilter, user?.conferenceId])

  const reloadPolls = async () => {
    await getConferenceBasedPolls(
      user?.role === Roles.SUPER_ADMIN && confFilter ? confFilter : user?.conferenceId
    ).then((data) => {
      dispatch(pollingSlice.actions.setPolls(data))
    })
  }

  useEffect(() => {
    const confTimezone =
      user?.role === Roles.ADMIN
        ? conference?.timezone
        : conferences?.find((conf) => conf?.id === confFilter)?.timezone
    const filteredPollsByEventOrSessionOrTalkDate =
      selectedDate === ALL_DATES_FILTER
        ? polls
        : polls?.filter(
            (poll) => moment(poll?.startDate).tz(confTimezone).format('M/D/YYYY') === selectedDate
          )

    const filteredPollsByStatus =
      selectedStatus === 'All'
        ? filteredPollsByEventOrSessionOrTalkDate
        : filteredPollsByEventOrSessionOrTalkDate.filter((poll) =>
            [PollStatus.STARTED, PollStatus.RESUMED].includes(selectedStatus)
              ? [PollStatus.STARTED, PollStatus.RESUMED].includes(poll.status)
              : poll.status === selectedStatus
          )

    const filteredPollsBySpeaker = selectedSpeaker?.id
      ? filteredPollsByStatus?.filter((poll) => poll.speakerId === selectedSpeaker?.id)
      : filteredPollsByStatus

    setFormattedPolls(filteredPollsBySpeaker)
  }, [polls, selectedDate, selectedStatus, selectedSpeaker?.id])

  useEffect(() => {
    if (polls?.length) {
      const confTimezone =
        user?.role === Roles.ADMIN
          ? conference?.timezone
          : conferences?.find((conf) => conf?.id === confFilter)?.timezone
      const DateFilters = polls?.reduce((filters, poll) => {
        const date = poll?.startDate
          ? moment(poll?.startDate).tz(confTimezone).format('M/D/YYYY')
          : null
        if (date && !filters?.includes(date)) {
          filters.push(date)
        }
        return filters
      }, [])

      if (!DateFilters.includes(selectedDate)) {
        dispatch(talkSlice.actions.setSelectedDate(ALL_DATES_FILTER))
      }
      setDateFilters([ALL_DATES_FILTER, ...DateFilters])
    }
  }, [polls])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: '5rem' }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box className={classes.headBar}>
        <Typography variant="h4">Polls</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {user?.role === Roles.ADMIN && (
            <Button variant="contained" color="primary" onClick={() => setOpenAddPollModal(true)}>
              Add Poll
            </Button>
          )}
          {user?.role === Roles.SUPER_ADMIN && (
            <Box className={classes.actions}>
              <Box className={classes.filterDropdown}>
                <Typography className={classes.filterLabel}>Conference: </Typography>
                {confProgress ? (
                  <CircularProgress size={25} />
                ) : (
                  <FormControl fullWidth variant="outlined" size="small" className={classes.select}>
                    <Select
                      value={confFilter}
                      onChange={(event) => {
                        dispatch(pollingSlice.actions.setConferenceFilter(event.target.value))
                        dispatch(pollingSlice.actions.setSelectedSpeaker('All'))
                        dispatch(pollingSlice.actions.setSelectedDate('All Dates'))
                        dispatch(pollingSlice.actions.setSelectedStatus('All'))
                      }}>
                      {orderedConferences?.map((conf) => (
                        <MenuItem value={conf?.id} key={conf?.id}>
                          {conf?.confName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      <Box className={classes.customCard}>
        <Typography className={classes.cardsInfo}>
          Use fields given below to filter the polls
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Box className={classes.dropdown}>
            <Typography variant="body2" className={classes.dropdownLabel}>
              Event/Session/Talk Date
            </Typography>
            <FormControl fullWidth variant="outlined" size="medium">
              <Select
                value={selectedDate}
                onChange={(event) => {
                  const value = event.target.value
                  dispatch(pollingSlice.actions.setSelectedDate(value))
                }}>
                {dateFilters?.map((filter) => (
                  <MenuItem value={filter} key={filter}>
                    {filter}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box className={classes.dropdown}>
            <Typography variant="body2" className={classes.dropdownLabel}>
              Status
            </Typography>
            <FormControl fullWidth variant="outlined" size="medium">
              <Select
                value={selectedStatus}
                onChange={(event) =>
                  dispatch(pollingSlice.actions.setSelectedStatus(event.target.value))
                }>
                <MenuItem value="All">All</MenuItem>
                {Object.values(PollStatus)?.map(
                  (status) =>
                    status !== 'resumed' && (
                      <MenuItem value={status} key={status}>
                        {getPollStatusText(status).status}
                      </MenuItem>
                    )
                )}
              </Select>
            </FormControl>
          </Box>
          <Box className={classes.dropdown}>
            <Typography variant="body2" className={classes.dropdownLabel}>
              Speaker
            </Typography>

            <Autocomplete
              variant="outlined"
              options={speakers}
              onChange={(e, value) => {
                dispatch(pollingSlice.actions.setSelectedSpeaker(value))
              }}
              getOptionLabel={(option) => option?.fullName}
              value={selectedSpeaker}
              renderOption={(option) => (
                <li className={classes.listItem}>
                  {option?.isSubject && option?.fullName
                    ? option?.fullName
                    : option?.isSubject && !option?.fullName
                    ? option.email
                    : option.fullName}
                  {option?.isSubject && option?.status === speakerStatus.PENDING && (
                    <Chip
                      size="small"
                      className={classes.chip}
                      color="primary"
                      label={
                        <Typography className={classes.chipLabel}>
                          {option?.status === speakerStatus.PENDING ? 'Not Approved' : 'Approved'}
                        </Typography>
                      }
                    />
                  )}
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} placeholder="Select speaker" variant="outlined" />
              )}
            />
          </Box>
        </Box>
      </Box>

      <Wrapper inProgress={loading}>
        <Paper variant="outlined" style={{ marginTop: '24px' }}>
          {formattedPolls?.length ? (
            <BasicTable
              data={formatTableData(formattedPolls)}
              columns={tableHead}
              options={{
                ...options,
                onChangePage: (page) => dispatch(pollingSlice.actions.setCurrentPage(page)),
                onChangeRowsPerPage: (size) => dispatch(pollingSlice.actions.setPageSize(size)),
                page: currentPage,
                rowsPerPage: pageSize
              }}
            />
          ) : (
            <ResourceNotFound
              message={
                selectedSpeaker === 'All'
                  ? `No polls found for this conference?.`
                  : 'No polls found for this speaker.'
              }
            />
          )}
        </Paper>
      </Wrapper>

      <Dialog fullWidth maxWidth="xl" open={openAddPollModal}>
        <DialogContent className={classes.pollModalContent}>
          <PollingForm
            forApproval={true}
            editedPollId={null}
            pollValues={pollValues}
            speakerId={null}
            conferenceId={
              user?.role === Roles.SUPER_ADMIN && confFilter ? confFilter : user?.conferenceId
            }
            associateSpeaker={true}
            subjectId={null}
            handleCloseModal={handleCloseModal}
            handleCloseConfirmModal={() => setOpenAddPollModal(false)}
            reloadPolls={reloadPolls}
          />
        </DialogContent>
      </Dialog>

      <Dialog fullWidth maxWidth="xl" open={Boolean(editedPoll?.id)}>
        <DialogContent className={classes.pollModalContent}>
          {questionsLoading ? (
            <Box className={classes.loader}>
              <CircularProgress />
            </Box>
          ) : (
            <PollingForm
              forApproval={true}
              editedPollId={editedPoll?.id}
              pollValues={pollValues}
              pollStatus={editedPoll?.status}
              speakerId={editedPoll?.speakerId}
              conferenceId={
                user?.role === Roles.SUPER_ADMIN && confFilter ? confFilter : user?.conferenceId
              }
              handleCloseModal={handleCloseModal}
              handleCloseConfirmModal={handleCloseConfirmModal}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog fullWidth maxWidth="sm" open={confirmModal} onClose={() => setConfirmModal(false)}>
        <Box className={classes.warningModalHeader}>
          <Warning className={classes.warningIcon} />
          <Typography className={classes.warningTitle}>Warning</Typography>
        </Box>
        <DialogContent className={classes.modalContent}>
          <Typography variant="body2">
            Any unsaved data will be lost. Press CANCEL to go back and save these changes, or OK to
            discard these changes.
          </Typography>

          <Box className={classes.actions}>
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={handleCloseConfirmModal}>
              ok
            </Button>
            <Button size="medium" onClick={() => setConfirmModal(false)}>
              cancel
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      <ConfirmModal
        dialogTitle="Delete Poll"
        dialogText="You're about to delete this poll, which may have responses to the questions. All existing answers will be lost. Are you sure you want to continue?"
        open={!!deletedPollId}
        closeModal={handleCloseDeleteModal}
        handleConfirm={handleDeletePoll}
        inProgress={isDeletingPoll}
      />
    </Box>
  )
}

export default PollsListing

const useStyles = makeStyles({
  listItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem'
  },
  chip: {
    background: '#c62828',
    color: '#FFFFFF'
  },
  chipLabel: {
    fontSize: '13px',
    letterSpacing: '0.5px',
    fontWeight: 'bold'
  },
  headBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: '1rem'
  },
  select: {
    minWidth: '200px'
  },
  filterDropdown: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    justifyContent: 'flex-end'
  },
  filterLabel: {
    width: '100%',
    fontSize: '1rem',
    textAlign: 'right',
    marginRight: '8px',
    fontWeight: 'bold'
  },
  pollModalContent: {
    paddingTop: '0px !important'
  },
  warningModalHeader: {
    padding: '20px 20px 10px 20px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  warningIcon: {
    color: '#ffa726'
  },
  warningTitle: {
    fontSize: '16px',
    fontWeight: 'bold'
  },
  modalContent: {
    padding: '4px 16px 4px 22px'
  },
  actions: {
    marginTop: '20px',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    marginBottom: '10px'
  },
  loader: {
    margin: '16px 0px',
    height: '100px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  subText: {
    marginTop: '4px',
    fontSize: '12px',
    color: '#757575'
  },
  customCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    padding: '1.25rem',
    border: '1px solid #bdbdbd',
    borderRadius: '12px',
    marginTop: '16px'
  },
  cardsInfo: {
    color: colors.indigo,
    fontSize: '16px',
    fontWeight: 500
  },
  dropdown: {
    width: '320px',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.2rem'
  },
  dropdownLabel: {
    fontSize: '12px',
    color: '#616161',
    whiteSpace: 'nowrap'
  },
  iconButton: {
    padding: '8px'
  }
})
